import React, { useState, useEffect, useRef, useCallback } from 'react';
import cn from 'classnames';
import debounce from 'lodash/debounce';

import Button from 'components/button/Button';
import Form from 'components/form/Form';
import TextInput from 'components/form-elements/text-input/TextInput';

import { IGlobalSearch } from 'source/NFR.types';

interface GlobalSearchProps extends IGlobalSearch {
  isOpen?: boolean;
  openGlobalSearch?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  closeGlobalSearch?: () => void;
}

const GlobalSearch: React.FC<GlobalSearchProps> = ({
  form,
  input,
  isOpen,
  button,
  openGlobalSearch,
  submitLabel,
  endpoint
}) => {
  const [width, setWidth] = useState<number | 'auto'>('auto');

  const containerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const focusTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const updateWidth = () => {
    if (containerRef.current) {
      setWidth(containerRef.current.offsetWidth || 'auto');
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const setWidthDebounced = useCallback(debounce(updateWidth, 500), []);

  useEffect(() => {
    setWidthDebounced();
    window.addEventListener('resize', setWidthDebounced);

    return () => {
      window.removeEventListener('resize', setWidthDebounced);
    };
  }, [setWidthDebounced]);

  useEffect(() => {
    if (isOpen) {
      if (focusTimeoutRef.current) clearTimeout(focusTimeoutRef.current);
      focusTimeoutRef.current = setTimeout(() => {
        inputRef.current?.focus();
      }, 300);
    }

    return () => {
      if (focusTimeoutRef.current) clearTimeout(focusTimeoutRef.current);
    };
  }, [isOpen]);

  return (
    <div className="global-search" ref={containerRef} style={{ width: isOpen ? 430 : width }}>
      <Form
        className={cn('global-search__form', {
          'global-search__form--is-visible': isOpen
        })}
        showSubmitButton={false}
        {...form}
      >
        <TextInput
          className="global-search__input"
          ref={inputRef}
          type="search"
          icon="search"
          theme={['rounded', 'hiddenLabel']}
          idPrefix="global"
          {...input}
        />
        <button className="global-search__submit" type="submit">
          {submitLabel}
        </button>
      </Form>
      <form action={endpoint}>
        <Button
          className="global-search--open"
          iconBeforeChildren="search"
          onClick={openGlobalSearch}
          {...button}
          type="submit"
        />
      </form>
    </div>
  );
};

GlobalSearch.displayName = 'GlobalSearch';

export default GlobalSearch;
